import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import { IMG } from "../data/site";
import { CalendarDays, ArrowRight, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useLang } from "../i18n/LanguageContext";
import { fetchEventById, fetchRecentEvents } from "../api";

const formatDate = (iso, lang = "en") =>
  iso ? new Date(iso).toLocaleDateString(lang === "kn" ? "kn-IN" : "en-IN", { day: "numeric", month: "long", year: "numeric" }) : "";

const COPY = {
  allEvents: { en: "All Events", kn: "ಎಲ್ಲಾ ಕಾರ್ಯಕ್ರಮಗಳು" },
  everyGathering: { en: "Every gathering, one place", kn: "ಎಲ್ಲಾ ಸಮಾವೇಶಗಳು ಒಂದೇ ಸ್ಥಳದಲ್ಲಿ" },
  readMore: { en: "Read more", kn: "ಇನ್ನಷ್ಟು ಓದಿ" },
  unableLoad: { en: "Unable to load events right now.", kn: "ಈಗ ಕಾರ್ಯಕ್ರಮಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗುತ್ತಿಲ್ಲ." },
  eventNotFound: { en: "Event not found", kn: "ಕಾರ್ಯಕ್ರಮ ಕಂಡುಬಂದಿಲ್ಲ" },
  loading: { en: "Loading...", kn: "ಲೋಡ್ ಆಗುತ್ತಿದೆ..." },
  loadingEvent: { en: "Loading event", kn: "ಕಾರ್ಯಕ್ರಮ ಲೋಡ್ ಆಗುತ್ತಿದೆ" },
  gallery: { en: "Gallery", kn: "ಗ್ಯಾಲರಿ" },
  galleryLabel: { en: "gallery", kn: "ಗ್ಯಾಲರಿ" },
};

export const EventsList = () => {
  const { t, lang } = useLang();
  const L = (copy) => (copy && copy[lang]) || copy.en;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    const loadEvents = async () => {
      try {
        const latestEvents = await fetchRecentEvents({ size: 9, index: 0 });
        if (!alive) return;
        setEvents(latestEvents);
        setError("");
      } catch (err) {
        if (!alive) return;
        setError("unableLoad");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => { alive = false; };
  }, []);

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("events.hero.eyebrow")}
        title={t("events.hero.title")}
        subtitle={t("events.hero.subtitle")}
        image={IMG.processional}
      />
      <section className="py-16 sm:py-20" data-testid="events-list">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={L(COPY.allEvents)} title={L(COPY.everyGathering)} align="center" />
          {loading && (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-live="polite">
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl overflow-hidden flex flex-col animate-pulse">
                  <div className="aspect-[16/10] bg-[#F2E7D5]" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-28 rounded bg-[#D4AF37]/30" />
                    <div className="h-6 w-full rounded bg-[#3B2A1A]/10" />
                    <div className="h-4 w-4/5 rounded bg-[#3B2A1A]/10" />
                    <div className="h-4 w-full rounded bg-[#3B2A1A]/10" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && error && (
            <div className="mt-12 rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7F0] px-5 py-4 text-sm text-[#3B2A1A]/80">
              {COPY[error] ? L(COPY[error]) : error}
            </div>
          )}
          {!loading && !error && (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((e) => (
                <Link
                  key={e.id}
                  to={`/events/${e.slug}`}
                  className="group bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl overflow-hidden card-lift flex flex-col"
                  data-testid={`event-card-${e.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={e.thumbnail || IMG.processional} alt={e.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="inline-flex items-center gap-2 text-xs text-[#B65A14] font-semibold">
                      <CalendarDays className="w-3.5 h-3.5" /> {formatDate(e.date, lang)}
                    </div>
                    <div className="font-serif-display text-xl text-[#3B2A1A] mt-2 leading-snug">{e.title}</div>
                    <div className="text-sm text-[#3B2A1A]/75 italic mt-1">{e.subtitle}</div>
                    <p className="mt-3 text-sm text-[#3B2A1A]/85 line-clamp-3 flex-1">{e.description}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-[#B65A14] text-sm font-semibold group-hover:gap-2 transition-all">
                      {L(COPY.readMore)} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export const EventDetail = () => {
  const { lang } = useLang();
  const L = (copy) => (copy && copy[lang]) || copy.en;
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;

    const loadEvent = async () => {
      try {
        const latestEvents = await fetchRecentEvents({ size: 9, index: 0 });
        const metadata = latestEvents.find((item) => item.slug === slug);
        const eventDetail = await fetchEventById(slug, { metadata });
        if (!alive) return;
        setEvent(eventDetail);
        setError("");
      } catch (err) {
        if (!alive) return;
        setError("eventNotFound");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    loadEvent();

    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <PageLayout>
        <PageHero
          eyebrow={L(COPY.loading)}
          title={L(COPY.loadingEvent)}
          subtitle=""
          image={IMG.processional}
        />
        <section className="py-16 sm:py-20" data-testid="event-detail">
          <div className="max-w-4xl mx-auto px-4 lg:px-8">
            <div className="h-5 w-28 rounded bg-[#D4AF37]/30 animate-pulse mb-8" />
            <div className="space-y-3 animate-pulse">
              <div className="h-5 w-full rounded bg-[#3B2A1A]/10" />
              <div className="h-5 w-5/6 rounded bg-[#3B2A1A]/10" />
              <div className="h-5 w-2/3 rounded bg-[#3B2A1A]/10" />
            </div>
          </div>
        </section>
      </PageLayout>
    );
  }

  if (error || !event) {
    return (
      <PageLayout>
        <div className="py-16 text-center max-w-2xl mx-auto px-4">
          <h1 className="font-serif-display text-3xl text-[#3B2A1A]">{COPY[error] ? L(COPY[error]) : error || L(COPY.eventNotFound)}</h1>
          <Link to="/events" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> {L(COPY.allEvents)}
          </Link>
        </div>
      </PageLayout>
    );
  }

  const showPrev = (e) => { if (e) e.stopPropagation(); setLightbox((i) => (i === null ? null : (i - 1 + event.images.length) % event.images.length)); };
  const showNext = (e) => { if (e) e.stopPropagation(); setLightbox((i) => (i === null ? null : (i + 1) % event.images.length)); };

  return (
    <PageLayout>
      <PageHero
        eyebrow={formatDate(event.date, lang)}
        title={event.title}
        subtitle={event.subtitle}
        image={event.thumbnail || event.images?.[0] || IMG.processional}
      />
      <section className="py-16 sm:py-20" data-testid="event-detail">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <Link to="/events" className="inline-flex items-center gap-2 text-[#B65A14] text-sm font-semibold mb-6 hover:text-[#8F4610]" data-testid="event-back">
            <ArrowLeft className="w-4 h-4" /> {L(COPY.allEvents)}
          </Link>
          <p className="text-lg text-[#3B2A1A]/85 leading-relaxed" data-testid="event-description">{event.description}</p>

          {event.images && event.images.length > 0 && (
            <div className="mt-12">
              <div className="overline text-[#B65A14] mb-4">{L(COPY.gallery)}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {event.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setLightbox(i)}
                    className="aspect-square overflow-hidden rounded-2xl border border-[#D4AF37]/25 card-lift"
                    data-testid={`event-image-${i}`}
                  >
                    <img src={src} alt={`${event.title} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent
          className="max-w-5xl bg-transparent border-0 p-0 shadow-none"
          aria-describedby={undefined}
          data-testid="event-lightbox"
        >
          {lightbox !== null && (
            <div className="relative flex items-center justify-center">
              <DialogTitle className="sr-only">{event.title} {L(COPY.galleryLabel)}</DialogTitle>
              {event.images.length > 1 && (
                <button onClick={showPrev} className="absolute left-2 sm:-left-14 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-[#FBF7F0]/90 text-[#3B2A1A] flex items-center justify-center shadow-lg" data-testid="event-lightbox-prev">
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <img src={event.images[lightbox]} alt={`${event.title} ${lightbox + 1}`} className="w-full h-auto max-h-[82vh] object-contain rounded-xl" />
              {event.images.length > 1 && (
                <button onClick={showNext} className="absolute right-2 sm:-right-14 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-[#FBF7F0]/90 text-[#3B2A1A] flex items-center justify-center shadow-lg" data-testid="event-lightbox-next">
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#3B2A1A]/90 to-transparent p-6 rounded-b-xl flex items-center justify-between">
                <span className="text-[#FBF7F0] font-serif-display text-lg">{event.title}</span>
                <span className="text-[#FBF7F0]/70 text-xs uppercase tracking-widest">{lightbox + 1} / {event.images.length}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

// default export = list, used by /events route
export default EventsList;
